import { ForecastPageLimitsResponse, ForecastSlice } from '@e2m-customer-portal/hooks';
import { InputFieldType, isInteger, NullableMeasure, parseNumber, Validation } from '@e2m-customer-portal/shared';
import React, { ChangeEvent, ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBandRules } from '../../../../../../hooks/use-bands-rules/use-bands-rules';
import { isKeyCombo } from '../../../../short-term-forecast/forecast-list/utils';
import { DeviceWithInstalledPower } from '../../../ControlEnergyForecast';
import { ProductInput } from '../utils';
import { Direction } from './../../../../utils';

interface ControlCellInputProps {
  device: DeviceWithInstalledPower;
  slice: ForecastSlice;
  limits: ForecastPageLimitsResponse;
  product: InputFieldType;
  productIndex: number;
  cellIndex: {
    row: number;
    column: number;
  };
  value?: string;
  disabled?: boolean;
  readOnly?: boolean;
  setValue: (value?: string) => void;
  setError: (value?: string) => void;
  setOverviewMap: React.Dispatch<React.SetStateAction<Map<string, ProductInput[][][]>>>;
  switchFocus: (sourceRow: number, sourceColumn: number, sourceProductIndex: number, direction?: Direction) => boolean;
  saveChanges: () => void;
}

export function ControlCellInput({
  device,
  slice,
  limits,
  product,
  productIndex,
  cellIndex,
  value,
  disabled,
  readOnly,
  setValue,
  setError,
  setOverviewMap,
  switchFocus,
  saveChanges,
}: Readonly<ControlCellInputProps>): ReactElement<ControlCellInputProps> {
  const [t] = useTranslation('validation');

  const bandRules: Validation<NullableMeasure>[] | undefined = useBandRules(product, limits);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;

      if (rawValue === '') {
        setError(t('COMMON_REQUIRED'));
      } else if (!isInteger(rawValue) || 0 > parseNumber(rawValue)) {
        setError(t('COMMON_MUST_CONTAIN_POSITIVE_INTEGER'));
      } else if (bandRules) {
        for (const rule of bandRules) {
          if (rule.validate({ value: parseNumber(rawValue), unit: 'kW' })) {
            setError(undefined);
          } else {
            setError(rule.message);
          }
        }
      } else {
        setError(undefined);
      }

      setValue(rawValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [device, slice],
  );

  const handleEscape = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.currentTarget.selectionStart !== event.currentTarget.selectionEnd) {
      event.currentTarget.setSelectionRange(event.currentTarget.selectionEnd, event.currentTarget.selectionEnd);
    } else {
      event.currentTarget.blur();
    }
    event.preventDefault();
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        handleEscape(event);
      } else if ((Object.values(Direction) as string[]).includes(event.key)) {
        if (switchFocus(cellIndex.row, cellIndex.column, productIndex, event.key as Direction)) {
          event.preventDefault();
        }
      } else if (event.key === 'Enter') {
        event.currentTarget.select();
        switchFocus(cellIndex.row, cellIndex.column, productIndex, event.shiftKey ? Direction.Up : Direction.Down);
        event.preventDefault();
      } else if (event.key === 'Tab') {
        event.currentTarget.select();
        switchFocus(cellIndex.row, cellIndex.column, productIndex, event.shiftKey ? Direction.Left : Direction.Right);
        event.preventDefault();
      } else if (isKeyCombo(event, 's')) {
        saveChanges();
        event.preventDefault();
      }
    },
    [cellIndex.column, cellIndex.row, handleEscape, productIndex, saveChanges, switchFocus],
  );

  const collectRef = useCallback(
    (input: HTMLInputElement) =>
      setOverviewMap((overviewMap) => {
        const deviceMap = overviewMap.get(device.id)!;
        if (!deviceMap[cellIndex.row][cellIndex.column]) {
          deviceMap[cellIndex.row].push(new Array<ProductInput>());
        }
        deviceMap[cellIndex.row][cellIndex.column][productIndex] = { product, input };
        return overviewMap;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cellIndex.column, cellIndex.row, device.id, product, productIndex],
  );

  return (
    <input
      ref={collectRef}
      className="w-[calc(100%-5rem)] border-none bg-transparent px-10 py-0 text-center leading-6 focus:cursor-text focus:outline-none disabled:pointer-events-none disabled:opacity-60"
      value={value}
      disabled={disabled}
      readOnly={readOnly}
      onFocus={(event) => {
        if (!readOnly) event.target.select();
      }}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
    />
  );
}
